/* useLiveEvents — subscribe to the server's per-user SSE stream and fan
 * events out to any component that wants to react.
 *
 * Usage:
 *   const { last } = useLiveEvents(["evidence.approved", "audit.updated"], (evt) => {
 *     refetch();
 *   });
 *
 * A single EventSource is shared across the app (mounted once by App/Layout).
 */
import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";

let _shared = { es: null, listeners: new Set(), lastByType: {} };

function ensureConnection() {
  if (_shared.es || typeof window === "undefined") return;
  try {
    const es = new EventSource(`${API}/events/stream`, { withCredentials: true });
    _shared.es = es;

    const dispatch = (payload) => {
      _shared.lastByType[payload.type] = payload;
      _shared.listeners.forEach((fn) => {
        try { fn(payload); } catch { /* isolate listener errors */ }
      });
    };

    // Generic message handler (SSE without 'event:' field lands here)
    es.onmessage = (e) => {
      try { dispatch(JSON.parse(e.data)); } catch { /* ignore */ }
    };

    // Typed events — we mirror the server's event names
    ["hello", "evidence.approved", "audit.updated", "bounty.resolved", "score.updated"]
      .forEach((t) => es.addEventListener(t, (e) => {
        try { dispatch(JSON.parse(e.data)); } catch { /* ignore */ }
      }));

    es.onerror = () => {
      // Let the browser auto-reconnect; if the stream stays broken, tear
      // down so the next mount reopens cleanly.
      if (es.readyState === EventSource.CLOSED) {
        _shared.es = null;
      }
    };
  } catch { /* older browsers / blocked */ }
}

export function closeLiveEvents() {
  if (_shared.es) {
    try { _shared.es.close(); } catch { /* noop */ }
    _shared.es = null;
  }
  _shared.lastByType = {};
}

/**
 * Subscribe to one or more event types.
 * @param {string[]} types
 * @param {(evt:{type:string,data:any,ts:string})=>void} handler
 */
export function useLiveEvents(types, handler) {
  const [last, setLast] = useState(null);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    ensureConnection();
    const filter = new Set(types || []);
    const listener = (payload) => {
      if (filter.size === 0 || filter.has(payload.type)) {
        setLast(payload);
        try { handlerRef.current && handlerRef.current(payload); } catch { /* noop */ }
      }
    };
    _shared.listeners.add(listener);
    return () => { _shared.listeners.delete(listener); };
  }, [Array.isArray(types) ? types.join("|") : String(types)]);

  return { last };
}
